import { Autocomplete, Avatar, Box, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

// General internal-user picker. With a `role` it lists only users holding that
// manna role (ae/bdr); without a role it lists all internal (non-partner) users
// — used for CSM and any free assignment. Backed by manna_list_users_with_role
// (migration 20260604000006 made p_role optional).

export interface UserOption {
  id: string;
  email: string;
  avatar_url: string | null;
  full_name: string | null;
}

export function userLabel(o: UserOption): string {
  return o.full_name?.trim() || o.email;
}

function useInternalUsers(role: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["internal_users", role ?? "all"],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<UserOption[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        "manna_list_users_with_role",
        role ? { p_role: role } : {},
      );
      if (error) throw error;
      return (data ?? []) as UserOption[];
    },
  });
}

export interface UserPickerProps {
  label: string;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Restrict to a specific manna role (ae/bdr/partner_rep). Omit for all internal users. */
  role?: "ae" | "bdr" | "partner_rep";
  disabled?: boolean;
  size?: "small" | "medium";
}

export function UserPicker({
  label,
  value,
  onChange,
  role,
  disabled,
  size = "small",
}: UserPickerProps) {
  const { data: options = [], isLoading } = useInternalUsers(role, !disabled || value != null);
  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete<UserOption>
      options={options}
      loading={isLoading}
      value={selected}
      disabled={disabled}
      onChange={(_, v) => onChange(v?.id ?? null)}
      getOptionLabel={userLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, o) => (
        <Box
          component="li"
          {...props}
          key={o.id}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <Avatar src={o.avatar_url ?? undefined} sx={{ width: 24, height: 24, fontSize: 12 }}>
            {(userLabel(o)[0] ?? "?").toUpperCase()}
          </Avatar>
          {userLabel(o)}
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
      fullWidth
      size={size}
    />
  );
}
